import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import EpsfPageBreadcrumb from "../EpsfPageBreadcrumb";
import ReportTable from "../../../common/ReportTable/ReportTable";
import Dropdown from "../../../common/dropDown";
import SegmentDatePicker from "../../../common/aqPickers/SegmentDatePicker";
import SegmentTimePicker from "../../../common/aqPickers/SegmentTimePicker";
import InfoAlert from "../../../common/InfoAlert";
import useInfoAlert from "../../../../hooks/useInfoAlert";
import { openAuthModal } from "../../../common/authmodal/authmodalservice";
import { openESignatureConfirmModal } from "../../../common/esignature/esignatureConfirmService";
import { EpsfAccessGate, useEpsfAccess } from "../epsfAccess";
import { formatReceiptStatusLabel, renderReceiptStatusBadge } from "../epsfBadges";
import {
  batchOptionLabel,
  findOption,
  getImpTxnRowKey,
  renderImpMovementCell,
  sanitizeEpsfDisplayLabel,
  toApiDateTimeString,
  useEpsfLedger,
  useEpsfStudySite,
} from "../epsfLedgerUi";
import {
  checkFormActionBlockers,
  confirmDestroyRequest,
  createDestroyRequest,
  ensureFormOccurrence,
  fetchFormWorkItems,
  fetchHeldReceipts,
  fetchImpKits,
  fetchReceiptDetail,
  formBlockersNeedRequest,
  formBlockMessageForAction,
  linkFormInstance,
  postImpDestroy,
  uploadReceiptEvidence,
} from "../epsfService";
import { createInstance } from "../../esource/esourceService";
import EpsfFormFillModal from "../EpsfFormFillModal";
import EvidencePdfThumb from "./EvidencePdfThumb";
import ImpQtyKitFloatCell, {
  txnKitNumberList,
} from "./ImpQtyKitFloatCell";
import { batchHeld, batchOnHand } from "./inventory/inventoryDashboardUtils";
import "../EpsfPages.scss";

const MAX_DESTROY_EVIDENCE = 3;

/** Statuses selectable in the Destroy kit tree (aligned with PostDestroy / Returns stock). */
const DESTROY_KIT_STATUSES = [
  "Available",
  "Released",
  "Returned",
  "RequiresReview",
  "Hold",
  "Quarantined",
  "ReturnPending",
  "DestructionPending",
];

const emptyEvidenceSlots = () =>
  Array.from({ length: MAX_DESTROY_EVIDENCE }, () => null);

const DESTROY_METHOD_OPTIONS = [
  { value: "", label: "Select destruction method" },
  { value: "Incineration", label: "Incineration" },
  { value: "Autoclave", label: "Autoclave / sterilisation" },
  { value: "ChemicalInactivation", label: "Chemical inactivation" },
  { value: "CrushingShredding", label: "Crushing / shredding" },
  { value: "ReturnToManufacturer", label: "Return to manufacturer / sponsor" },
  { value: "PharmacyDestruction", label: "Pharmacy destruction (local)" },
  { value: "Other", label: "Other" },
];

const DESTROY_REASON_OPTIONS = [
  { value: "", label: "Select reason" },
  { value: "Expired", label: "Expired" },
  { value: "Damaged", label: "Damaged" },
  { value: "TemperatureExcursion", label: "Temperature excursion" },
  { value: "Recall", label: "Recall" },
  { value: "RejectedAfterReview", label: "Rejected after review" },
  { value: "SponsorInstruction", label: "Sponsor instruction" },
  { value: "ExcessStock", label: "Excess stock" },
  { value: "Other", label: "Other" },
];

function pad2(n) {
  return String(n).padStart(2, "0");
}

function nowTimeHHmm() {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function heldQtyOf(row) {
  const explicit = row?.HeldQuantity ?? row?.heldQuantity;
  if (explicit != null && explicit !== "") {
    const n = Number(explicit);
    return Number.isFinite(n) ? n : 0;
  }
  return Math.max(
    0,
    Number(row?.Quantity ?? row?.quantity ?? 0) -
      Number(row?.ReleasedQuantity ?? row?.releasedQuantity ?? 0) -
      Number(
        row?.ExternallyReturnedQuantity ?? row?.externallyReturnedQuantity ?? 0,
      ),
  );
}

function destructionPendingKitCount(row) {
  const n = Number(
    row?.DestructionPendingKitCount ?? row?.destructionPendingKitCount ?? 0,
  );
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

function isDestructionPendingSource(row) {
  const status = String(row?.ReceiptStatus || row?.receiptStatus || "").trim();
  return status === "DestructionPending" || destructionPendingKitCount(row) > 0;
}

function kitsForPendingDestroy(heldRow, kits) {
  const txnId = String(heldRow?.ImpTxnId || heldRow?.impTxnId || "")
    .trim()
    .toLowerCase();
  if (!txnId) return [];
  return (kits || []).filter((k) => {
    if (String(k.Status || k.status || "") !== "DestructionPending") return false;
    return (
      String(k.ReceiptTxnId || k.receiptTxnId || "")
        .trim()
        .toLowerCase() === txnId
    );
  });
}

function pendingDestroyUnits(heldRow, kits = []) {
  const kitRows = kitsForPendingDestroy(heldRow, kits);
  if (kitRows.length > 0) return kitRows.length;
  const apiKits = destructionPendingKitCount(heldRow);
  if (apiKits > 0) return apiKits;
  if (!isDestructionPendingSource(heldRow)) return 0;
  const held = heldQtyOf(heldRow);
  return held > 0 ? held : 1;
}

function pendingDestroyToRow(h, kits = []) {
  const id = String(h.ImpTxnId || h.impTxnId || "");
  const held = heldQtyOf(h);
  const pendingKitRows = kitsForPendingDestroy(h, kits);
  const pendingKits =
    pendingKitRows.length > 0
      ? pendingKitRows.length
      : destructionPendingKitCount(h);
  const isKit =
    h.InventoryTrackingMethod === "IndividualKit" ||
    h.inventoryTrackingMethod === "IndividualKit" ||
    pendingKits > 0;
  const qty = pendingKits > 0 ? pendingKits : held;
  const kitNumbers = pendingKitRows
    .map((k) => k.KitNumber || k.kitNumber)
    .filter(Boolean)
    .join(", ");
  return {
    ...h,
    ImpTxnId: id,
    _pendingDestroy: true,
    TxnAt: h.TxnAt || h.txnAt || null,
    ProductCode: h.ProductCode || h.productCode,
    ProductName: h.ProductName || h.productName,
    BatchNumber: h.BatchNumber || h.batchNumber,
    Quantity: qty,
    KitCount: isKit ? qty : null,
    KitNumbers: kitNumbers || null,
    ReceiptStatus: "DestructionPending",
    ConditionCode: null,
    ReferenceNo: null,
    CreatedByName: null,
  };
}

function txnAtMs(row) {
  const t = new Date(row?.TxnAt || row?.txnAt || 0).getTime();
  return Number.isFinite(t) ? t : 0;
}

function destroySortRank(row) {
  if (row?._pendingDestroy) return 0;
  return 1;
}

function pctChange(current, previous) {
  const cur = Number(current) || 0;
  const prev = Number(previous) || 0;
  if (prev === 0) {
    if (cur === 0) return 0;
    return 100;
  }
  return Math.round(((cur - prev) / prev) * 1000) / 10;
}

function formatPctChange(pct) {
  const n = Number(pct);
  if (!Number.isFinite(n)) return "0%";
  if (n > 0) return `+${n}%`;
  return `${n}%`;
}

function trendTone(pct) {
  const n = Number(pct);
  if (!Number.isFinite(n) || n === 0) return "is-flat";
  return n > 0 ? "is-up" : "is-down";
}

function trendIconClass(pct) {
  const n = Number(pct);
  if (!Number.isFinite(n) || n === 0) return "fa-minus";
  return n > 0 ? "fa-arrow-trend-up" : "fa-arrow-trend-down";
}

function startOfWeekMonday(d) {
  const x = new Date(d);
  const day = x.getDay();
  const diff = (day + 6) % 7;
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - diff);
  return x;
}

function PendingDestroyActionCell({ row, onComplete, busy = false }) {
  return (
    <div className="epsf-return-action-required">
      <span className="epsf-return-action-required__label">Action required</span>
      <button
        type="button"
        className="epsf-return-action-required__link"
        disabled={busy}
        onClick={() => onComplete?.(row)}
      >
        Complete destruction
      </button>
    </div>
  );
}

const DESTROY_FIELD_OPTIONS = [
  { key: "Product", label: "IMP" },
  { key: "BatchNumber", label: "Batch / Lot" },
  { key: "Quantity", label: "Qty" },
  { key: "ReceiptStatus", label: "Status" },
  { key: "FormDue", label: "Forms" },
  { key: "TxnAt", label: "When" },
  { key: "CreatedByName", label: "Recorded by" },
  { key: "ReferenceNo", label: "Reference" },
  { key: "KitCount", label: "Kits" },
];

const DESTROY_DEFAULT_COLUMNS = [
  "Product",
  "BatchNumber",
  "Quantity",
  "ReceiptStatus",
  "FormDue",
  "TxnAt",
  "CreatedByName",
];

function formBlockMessageForPendingDestroy(result, actionLabel) {
  const titles = (() => {
    const rows = Array.isArray(result?.rows) ? result.rows : [];
    return rows
      .map(
        (b) =>
          b.FormTitle ||
          b.formTitle ||
          b.Title ||
          b.title ||
          "Required form",
      )
      .filter(Boolean);
  })();
  if (!titles.length) return "";
  const label = actionLabel || "Confirm destruction";
  return `${label} is blocked until these form(s) are completed and approved: ${titles.join("; ")}. Use Fill form on this In destruction row.`;
}

function isDestroyFormTrigger(trigger) {
  const t = String(trigger || "").toUpperCase();
  return (
    t === "DESTRUCTION_REQUESTED" ||
    t === "ONDESTROY" ||
    t === "DESTROY"
  );
}

function destroyWorkItemStatus(row) {
  return String(
    row?.WorkItemStatus || row?.workItemStatus || row?.Status || row?.status || "",
  ).toUpperCase();
}

function destroyWorkItemType(row) {
  return String(row?.WorkType || row?.workType || "COMPLETE").toUpperCase();
}

function destroyWorkflowStatus(row) {
  return String(row?.WorkflowStatus || row?.workflowStatus || "").toUpperCase();
}

function summarizeDestroyFormItems(items = []) {
  const list = Array.isArray(items) ? items : [];
  const openFill = list.filter((row) => {
    const st = destroyWorkItemStatus(row);
    const wt = destroyWorkItemType(row);
    return st === "OPEN" && (wt === "COMPLETE" || wt === "CORRECT");
  });
  if (openFill.length) return { phase: "fill", items: openFill, label: "Fill form" };
  const openReview = list.filter(
    (row) =>
      destroyWorkItemStatus(row) === "OPEN" &&
      destroyWorkItemType(row) === "REVIEW",
  );
  if (openReview.length) {
    return { phase: "approve", items: openReview, label: "Approval pending" };
  }
  const locked = list.filter((row) => {
    const wf = destroyWorkflowStatus(row);
    const occ = String(
      row?.OccurrenceStatus || row?.occurrenceStatus || "",
    ).toUpperCase();
    return (
      wf === "APPROVED" ||
      wf === "COMPLETED" ||
      occ === "COMPLETE" ||
      occ === "COMPLETED"
    );
  });
  if (locked.length) {
    return {
      phase: "locked",
      items: [locked[0]],
      label:
        destroyWorkflowStatus(locked[0]) === "APPROVED" ? "Approved" : "Completed",
    };
  }
  return { phase: "none", items: [], label: "" };
}

function pendingDestroySourceSummary(h, batchRow = null) {
  if (!h && !batchRow) return "—";
  const product =
    sanitizeEpsfDisplayLabel(
      h?.ProductName ||
        h?.productName ||
        batchRow?.ProductName ||
        batchRow?.productName,
    ) ||
    h?.ProductCode ||
    batchRow?.ProductCode ||
    "Study kit";
  const batch =
    h?.BatchNumber ||
    h?.batchNumber ||
    batchRow?.BatchNumber ||
    batchRow?.batchNumber ||
    "—";
  const held = h ? heldQtyOf(h) : 0;
  const pending = h ? destructionPendingKitCount(h) : 0;
  const status =
    formatReceiptStatusLabel(h?.ReceiptStatus || h?.receiptStatus) ||
    h?.ReceiptStatus ||
    "";
  const onHand = batchRow != null ? batchOnHand(batchRow) : null;
  const parts = [
    `${product} / ${batch}`,
    onHand != null ? `on hand ${onHand}` : null,
    held > 0 ? `held ${held}` : null,
    pending > 0 ? `${pending} destruction pending` : null,
    status || null,
  ].filter(Boolean);
  return parts.join(" · ");
}

function isAllowedDestroyEvidenceFile(file) {
  if (!file) return false;
  const type = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  if (type.startsWith("image/") || type === "application/pdf") return true;
  return (
    name.endsWith(".pdf") ||
    name.endsWith(".png") ||
    name.endsWith(".jpg") ||
    name.endsWith(".jpeg") ||
    name.endsWith(".webp") ||
    name.endsWith(".gif")
  );
}

function isImageEvidenceFile(file) {
  if (!file) return false;
  const type = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  if (type.startsWith("image/")) return true;
  return (
    name.endsWith(".png") ||
    name.endsWith(".jpg") ||
    name.endsWith(".jpeg") ||
    name.endsWith(".webp") ||
    name.endsWith(".gif")
  );
}

function isPdfEvidenceFile(file) {
  if (!file) return false;
  const type = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  return type === "application/pdf" || name.endsWith(".pdf");
}

function evidenceTypeForFile(file) {
  if (isImageEvidenceFile(file)) return "DestructionPhoto";
  return "DestructionCertificate";
}

const emptyForm = () => ({
  impBatchId: "",
  sourceTxnId: "",
  quantity: "",
  txnDate: new Date(),
  txnTime: nowTimeHHmm(),
  reason: "",
  method: "",
  referenceNo: "",
  selectedKitIds: [],
});

export default function ImpDestruction({
  overlay = false,
  preferImpProductId = null,
  preferImpBatchId = null,
  preferSourceTxnId = null,
  onOverlayClose = null,
  onOverlaySuccess = null,
} = {}) {
  const { loading, canView, moduleAccess, canDestroy } = useEpsfAccess();
  const allow = canView && moduleAccess;
  const ctx = useEpsfStudySite();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const {
    balances,
    txns,
    error,
    setError,
    refresh: refreshLedger,
    loading: listLoading,
  } = useEpsfLedger(ctx.studyId, ctx.siteId, "Destroy");

  const preferProductKey = String(preferImpProductId || "").toLowerCase();
  const preferBatchId = String(preferImpBatchId || "").trim();
  const preferSourceId = String(preferSourceTxnId || "").trim();

  const [showModal, setShowModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [formBlocked, setFormBlocked] = useState(false);
  const [formBlockMessage, setFormBlockMessage] = useState("");
  const [formGateBusy, setFormGateBusy] = useState(false);
  const [needsRequest, setNeedsRequest] = useState(false);
  const [requestBusy, setRequestBusy] = useState(false);
  const [formDueByTxn, setFormDueByTxn] = useState({});
  const [formDueTick, setFormDueTick] = useState(0);
  const [formFillBusy, setFormFillBusy] = useState(false);
  const [formFillModal, setFormFillModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [destroyFromPending, setDestroyFromPending] = useState(false);
  const [kits, setKits] = useState([]);
  const [siteKits, setSiteKits] = useState([]);
  const [heldSources, setHeldSources] = useState([]);
  const [evidenceSlots, setEvidenceSlots] = useState(emptyEvidenceSlots);
  const [stockTrackOverview, setStockTrackOverview] = useState(null);
  const [stockTrackBusy, setStockTrackBusy] = useState(false);
  const [destroyKitSearch, setDestroyKitSearch] = useState("");
  const [collapsedKitTreeBatches, setCollapsedKitTreeBatches] = useState(
    () => new Set(),
  );
  /** Avoid re-ensuring DESTRUCTION_REQUESTED for the same held txn every render. */
  const ensuredDestroyFormTxnRef = useRef(new Set());

  const evidencePreviewUrls = useMemo(
    () =>
      (evidenceSlots || []).map((file) => {
        if (!file) return null;
        if (isImageEvidenceFile(file) || isPdfEvidenceFile(file)) {
          return URL.createObjectURL(file);
        }
        return null;
      }),
    [evidenceSlots],
  );

  useEffect(() => {
    return () => {
      (evidencePreviewUrls || []).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [evidencePreviewUrls]);

  const refreshHeldSources = useCallback(async () => {
    if (!ctx.studyId || !ctx.siteId) {
      setHeldSources([]);
      setSiteKits([]);
      return;
    }
    const [hRes, kRes] = await Promise.all([
      fetchHeldReceipts(ctx.studyId, ctx.siteId),
      fetchImpKits(ctx.studyId, ctx.siteId),
    ]);
    if (hRes.success) setHeldSources(hRes.rows || []);
    if (kRes.success) setSiteKits(kRes.rows || []);
  }, [ctx.studyId, ctx.siteId]);

  const refresh = useCallback(async () => {
    await Promise.all([refreshLedger(), refreshHeldSources()]);
  }, [refreshLedger, refreshHeldSources]);

  useEffect(() => {
    refreshHeldSources();
  }, [refreshHeldSources]);

  const pendingDestroySources = useMemo(
    () => (heldSources || []).filter(isDestructionPendingSource),
    [heldSources],
  );

  const unifiedTableRows = useMemo(() => {
    const pending = (pendingDestroySources || []).map((h) =>
      pendingDestroyToRow(h, siteKits),
    );
    const done = (txns || []).map((r) => ({
      ...r,
      _pendingDestroy: false,
      ReceiptStatus: r.ReceiptStatus || r.receiptStatus || "Destroyed",
      KitNumbers:
        r.KitNumbers ||
        r.kitNumbers ||
        txnKitNumberList(r).join(", ") ||
        null,
    }));
    return [...pending, ...done].sort((a, b) => {
      const rank = destroySortRank(a) - destroySortRank(b);
      if (rank !== 0) return rank;
      return txnAtMs(b) - txnAtMs(a);
    });
  }, [pendingDestroySources, siteKits, txns]);

  const refreshFormDues = useCallback(() => {
    setFormDueTick((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ctx.studyId || !ctx.siteId) {
        setFormDueByTxn({});
        return;
      }
      try {
        const work = await fetchFormWorkItems(ctx.studyId, ctx.siteId, "*");
        if (cancelled) return;
        const map = {};
        (work.rows || []).forEach((row) => {
          const txnId = String(
            row.LinkedTxnId || row.linkedTxnId || "",
          ).toLowerCase();
          if (
            !txnId ||
            !isDestroyFormTrigger(row.TriggerCode || row.triggerCode)
          ) {
            return;
          }
          if (!map[txnId]) map[txnId] = [];
          map[txnId].push(row);
        });
        setFormDueByTxn(map);
      } catch {
        if (!cancelled) setFormDueByTxn({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ctx.studyId, ctx.siteId, txns, heldSources, formDueTick]);

  /* In destruction (held DestructionPending): ensure form due so Fill form appears — not Create request. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ctx.studyId || !ctx.siteId) return;
      const pending = pendingDestroySources || [];
      if (!pending.length) return;

      let ensuredAny = false;
      for (const h of pending) {
        const txnId = String(h.ImpTxnId || h.impTxnId || "").trim();
        if (!txnId) continue;
        const key = txnId.toLowerCase();
        if (ensuredDestroyFormTxnRef.current.has(key)) continue;
        /* Already have open/done work for this txn — skip. */
        if ((formDueByTxn[key] || []).length) {
          ensuredDestroyFormTxnRef.current.add(key);
          continue;
        }
        try {
          await ensureFormOccurrence({
            StudyId: ctx.studyId,
            SiteId: ctx.siteId,
            TriggerCode: "DESTRUCTION_REQUESTED",
            LinkedTxnId: txnId,
          });
          ensuredAny = true;
        } catch {
          /* non-fatal — gate / list still refresh */
        } finally {
          ensuredDestroyFormTxnRef.current.add(key);
        }
      }
      if (!cancelled && ensuredAny) refreshFormDues();
    })();
    return () => {
      cancelled = true;
    };
  }, [
    ctx.studyId,
    ctx.siteId,
    pendingDestroySources,
    formDueByTxn,
    refreshFormDues,
  ]);

  const destroyTableRows = useMemo(
    () =>
      (unifiedTableRows || []).map((row) => {
        const txnId = String(row.ImpTxnId || row.impTxnId || "").toLowerCase();
        const dues = formDueByTxn[txnId] || [];
        const summary = summarizeDestroyFormItems(dues);
        return {
          ...row,
          FormDuePhase: summary.phase,
          FormDueLabel: summary.label,
          FormDueItems: summary.items,
          FormDue: summary.label || "",
        };
      }),
    [unifiedTableRows, formDueByTxn],
  );

  const destroySummary = useMemo(() => {
    const pending = (pendingDestroySources || []).reduce(
      (sum, h) => sum + pendingDestroyUnits(h, siteKits),
      0,
    );
    const destroyed = (txns || []).length;
    const kitsDestroyed = (txns || []).reduce((sum, r) => {
      const kits = Number(r.KitCount ?? r.kitCount ?? 0);
      if (kits > 0) return sum + kits;
      const qty = Number(r.Quantity ?? r.quantity ?? 0);
      return sum + (Number.isFinite(qty) && qty > 0 ? qty : 0);
    }, 0);
    return { pending, destroyed, kitsDestroyed };
  }, [pendingDestroySources, siteKits, txns]);

  const weeklyTrend = useMemo(() => {
    const thisWeekStart = startOfWeekMonday(new Date());
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);
    let thisWeek = 0;
    let lastWeek = 0;
    for (const r of txns || []) {
      const t = new Date(r.TxnAt || r.txnAt || 0);
      if (Number.isNaN(t.getTime())) continue;
      if (t >= thisWeekStart) thisWeek += 1;
      else if (t >= lastWeekStart) lastWeek += 1;
    }
    return {
      thisWeek,
      lastWeek,
      completedPct: pctChange(thisWeek, lastWeek),
    };
  }, [txns]);

  const batchOptions = useMemo(
    () =>
      balances
        .filter((b) => Number(b.OnHand ?? b.onHand ?? 0) > 0)
        .filter((b) => {
          if (!preferProductKey) return true;
          return (
            String(b.ImpProductId || b.impProductId || "").toLowerCase() ===
            preferProductKey
          );
        })
        .map((b) => ({
          value: String(b.ImpBatchId || b.impBatchId),
          label: batchOptionLabel(b),
          row: b,
        })),
    [balances, preferProductKey],
  );

  const selectedBatch = useMemo(
    () => batchOptions.find((o) => o.value === form.impBatchId)?.row || null,
    [batchOptions, form.impBatchId],
  );

  const selectedSource = useMemo(() => {
    const sourceId = String(form.sourceTxnId || "")
      .trim()
      .toLowerCase();
    if (!sourceId) return null;
    return (
      heldSources.find(
        (h) =>
          String(h.ImpTxnId || h.impTxnId || "")
            .trim()
            .toLowerCase() === sourceId,
      ) || null
    );
  }, [form.sourceTxnId, heldSources]);

  const isIndividualKit = useMemo(() => {
    const fromBatch =
      String(
        selectedBatch?.InventoryTrackingMethod ||
          selectedBatch?.inventoryTrackingMethod ||
          "",
      ) === "IndividualKit";
    if (fromBatch) return true;
    const fromSource =
      String(
        selectedSource?.InventoryTrackingMethod ||
          selectedSource?.inventoryTrackingMethod ||
          "",
      ) === "IndividualKit";
    if (fromSource) return true;
    if (destructionPendingKitCount(selectedSource) > 0) return true;
    if ((kits || []).some((k) => k.ImpKitId || k.impKitId)) return true;
    return false;
  }, [selectedBatch, selectedSource, kits]);

  const autoDestroyQty = useMemo(() => {
    if (isIndividualKit) return form.selectedKitIds.length;
    if (selectedSource) {
      const held = heldQtyOf(selectedSource);
      if (held > 0) return held;
    }
    if (selectedBatch) {
      const held = batchHeld(selectedBatch);
      if (held > 0) return held;
      const onHand = batchOnHand(selectedBatch);
      if (onHand > 0) return onHand;
    }
    return Number(form.quantity) || 0;
  }, [
    isIndividualKit,
    form.selectedKitIds,
    form.quantity,
    selectedSource,
    selectedBatch,
  ]);

  const pendingSourceSummary = useMemo(
    () => pendingDestroySourceSummary(selectedSource, selectedBatch),
    [selectedSource, selectedBatch],
  );

  const eligibleKits = useMemo(() => {
    const sourceId = String(form.sourceTxnId || "")
      .trim()
      .toLowerCase();
    const batchId = String(form.impBatchId || "")
      .trim()
      .toLowerCase();
    const pool = (siteKits || []).length ? siteKits : kits;

    return (pool || []).filter((k) => {
      if (
        batchId &&
        String(k.ImpBatchId || k.impBatchId || "")
          .trim()
          .toLowerCase() !== batchId
      ) {
        return false;
      }
      const status = String(k.Status || k.status || "");
      if (!DESTROY_KIT_STATUSES.includes(status)) return false;

      if (destroyFromPending) {
        if (sourceId) {
          const receiptId = String(k.ReceiptTxnId || k.receiptTxnId || "")
            .trim()
            .toLowerCase();
          if (receiptId && receiptId !== sourceId) return false;
        }
        return status === "DestructionPending";
      }

      /* New destroy: pending kits are selectable in the tree (same batch). */
      return true;
    });
  }, [kits, siteKits, form.sourceTxnId, form.impBatchId, destroyFromPending]);

  /* Right panel: destroyable stock kits grouped by batch (Returns-style tree). */
  const destroyKitGroups = useMemo(() => {
    const groups = [];
    const seenBatch = new Set();

    const batchList =
      destroyFromPending && selectedBatch
        ? [selectedBatch]
        : (batchOptions || []).map((o) => o.row).filter(Boolean);

    batchList.forEach((b) => {
      const batchId = String(b.ImpBatchId || b.impBatchId || "");
      if (!batchId || seenBatch.has(batchId.toLowerCase())) return;
      seenBatch.add(batchId.toLowerCase());

      const method = String(
        b.InventoryTrackingMethod || b.inventoryTrackingMethod || "",
      );
      const productCode =
        b.ProductCode || b.productCode || b.ProductName || b.productName || "";
      const batchNumber = b.BatchNumber || b.batchNumber || "";
      const onHand = Number(b.OnHand ?? b.onHand ?? 0);
      const held = Number(b.HeldQuantity ?? b.heldQuantity ?? 0);

      if (method === "IndividualKit") {
        const sourceId = String(form.sourceTxnId || "")
          .trim()
          .toLowerCase();
        const pendingOnly = destroyFromPending;

        const batchKits = (siteKits || []).filter((k) => {
          if (
            String(k.ImpBatchId || k.impBatchId || "")
              .trim()
              .toLowerCase() !== batchId.toLowerCase()
          ) {
            return false;
          }
          const status = String(k.Status || k.status || "");
          if (!DESTROY_KIT_STATUSES.includes(status)) return false;
          if (pendingOnly && status !== "DestructionPending") return false;
          if (sourceId && pendingOnly) {
            const receiptId = String(k.ReceiptTxnId || k.receiptTxnId || "")
              .trim()
              .toLowerCase();
            if (receiptId && receiptId !== sourceId) return false;
          }
          return true;
        });

        if (!batchKits.length) return;
        groups.push({
          type: "kits",
          batchId,
          productCode,
          batchNumber,
          onHand,
          held,
          kits: batchKits.map((k) => ({
            ImpKitId: String(k.ImpKitId || k.impKitId || ""),
            KitNumber: k.KitNumber || k.kitNumber || "",
            Status: k.Status || k.status || "",
            ImpBatchId: batchId,
            ReceiptTxnId: k.ReceiptTxnId || k.receiptTxnId || "",
          })),
        });
        return;
      }

      if (onHand <= 0 && held <= 0) return;
      groups.push({
        type: "batch",
        batchId,
        productCode,
        batchNumber,
        onHand,
        held,
        kits: [],
      });
    });

    return groups;
  }, [
    batchOptions,
    siteKits,
    selectedBatch,
    destroyFromPending,
    form.sourceTxnId,
  ]);

  const filteredDestroyKitGroups = useMemo(() => {
    const q = String(destroyKitSearch || "")
      .trim()
      .toLowerCase();
    if (!q) return destroyKitGroups;
    return destroyKitGroups
      .map((group) => {
        const batchHit = [group.productCode, group.batchNumber, group.batchId]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
        if (group.type !== "kits") {
          return batchHit ? group : null;
        }
        if (batchHit) return group;
        const nextKits = (group.kits || []).filter((k) => {
          const no = String(k.KitNumber || k.kitNumber || k.ImpKitId || "");
          const st = String(k.Status || k.status || "");
          return (
            no.toLowerCase().includes(q) || st.toLowerCase().includes(q)
          );
        });
        if (!nextKits.length) return null;
        return { ...group, kits: nextKits };
      })
      .filter(Boolean);
  }, [destroyKitGroups, destroyKitSearch]);

  const selectedDestroyKitBatchSummary = useMemo(() => {
    if (!selectedBatch && !form.selectedKitIds.length) return null;
    return {
      productCode:
        selectedBatch?.ProductCode ||
        selectedBatch?.productCode ||
        selectedBatch?.ProductName ||
        selectedBatch?.productName ||
        "",
      batchNumber:
        selectedBatch?.BatchNumber || selectedBatch?.batchNumber || "",
      onHand: selectedBatch
        ? Number(selectedBatch.OnHand ?? selectedBatch.onHand ?? 0)
        : 0,
      held: selectedBatch
        ? Number(
            selectedBatch.HeldQuantity ?? selectedBatch.heldQuantity ?? 0,
          )
        : 0,
      selectedCount: form.selectedKitIds.length,
    };
  }, [selectedBatch, form.selectedKitIds]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (
        !showModal ||
        !ctx.studyId ||
        !ctx.siteId ||
        !form.sourceTxnId
      ) {
        setStockTrackOverview(null);
        return;
      }
      setStockTrackBusy(true);
      try {
        const detail = await fetchReceiptDetail(
          ctx.studyId,
          ctx.siteId,
          form.sourceTxnId,
        );
        if (!cancelled) {
          const ds = detail.ds || {};
          const tables = Object.keys(ds)
            .filter((k) => Array.isArray(ds[k]))
            .map((k) => ds[k]);
          const overview = tables[0]?.[0] || detail.rows?.[0] || null;
          setStockTrackOverview(overview);
        }
      } finally {
        if (!cancelled) setStockTrackBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showModal, ctx.studyId, ctx.siteId, form.sourceTxnId]);

  const stockInitiatedBy = useMemo(() => {
    const o = stockTrackOverview;
    if (!o) return "";
    return (
      o.ReleasedByName ||
      o.releasedByName ||
      o.ReceivedByName ||
      o.receivedByName ||
      ""
    );
  }, [stockTrackOverview]);

  const stockInitiatedAt = useMemo(() => {
    const o = stockTrackOverview;
    if (!o) return "";
    const raw = o.ReleasedAt || o.releasedAt || o.TxnAt || o.txnAt || "";
    if (!raw) return "";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleString();
  }, [stockTrackOverview]);

  const stockReviewComment = useMemo(() => {
    const o = stockTrackOverview;
    if (!o) return "";
    return String(o.ReleaseComment || o.releaseComment || "").trim();
  }, [stockTrackOverview]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (
        !showModal ||
        !ctx.studyId ||
        !ctx.siteId ||
        !form.impBatchId ||
        !isIndividualKit
      ) {
        setKits([]);
        return;
      }
      const batchId = String(form.impBatchId);
      const result = await fetchImpKits(ctx.studyId, ctx.siteId, {
        impBatchId: batchId,
      });
      if (cancelled) return;
      if (result.success) setKits(result.rows || []);
      else setKits([]);
    })();
    return () => {
      cancelled = true;
    };
  }, [showModal, ctx.studyId, ctx.siteId, form.impBatchId, isIndividualKit]);

  useEffect(() => {
    if (!showModal || !isIndividualKit || !destroyFromPending) return;
    if (!form.sourceTxnId) return;
    if (!isDestructionPendingSource(selectedSource)) return;
    const pendingIds = eligibleKits
      .filter((k) => String(k.Status || k.status) === "DestructionPending")
      .map((k) => String(k.ImpKitId || k.impKitId))
      .filter(Boolean);
    if (!pendingIds.length) return;
    setForm((prev) => {
      const same =
        prev.selectedKitIds.length === pendingIds.length &&
        pendingIds.every((id) => prev.selectedKitIds.includes(id));
      if (same) return prev;
      return {
        ...prev,
        selectedKitIds: pendingIds,
        quantity: String(pendingIds.length),
      };
    });
  }, [
    showModal,
    isIndividualKit,
    destroyFromPending,
    form.sourceTxnId,
    selectedSource,
    eligibleKits,
  ]);

  const openDestroyFromPending = useCallback(
    (heldRow) => {
      const batchId = String(heldRow?.ImpBatchId || heldRow?.impBatchId || "");
      const txnId = String(heldRow?.ImpTxnId || heldRow?.impTxnId || "");
      setDestroyFromPending(true);
      setForm({
        ...emptyForm(),
        impBatchId: batchId,
        sourceTxnId: txnId,
      });
      setEvidenceSlots(emptyEvidenceSlots());
      setKits([]);
      setDestroyKitSearch("");
      setCollapsedKitTreeBatches(new Set());
      setFormError("");
      setError("");
      setFormBlocked(false);
      setFormBlockMessage("");
      setShowModal(true);
    },
    [setError],
  );

  const renderCell = useCallback(
    (row, field) => {
      if (field === "FormDue") {
        const phase = row.FormDuePhase || "none";
        if (phase === "none") return "—";
        const label = row.FormDueLabel || "Fill form";
        const toneClass =
          phase === "fill"
            ? "epsf-receipt-form-due--fill"
            : phase === "approve"
              ? "epsf-receipt-form-due--approve"
              : "epsf-receipt-form-due--locked";
        return (
          <button
            type="button"
            className={`epsf-receipt-form-due epsf-receipt-form-due--action ${toneClass}`}
            disabled={formFillBusy}
            onClick={async (e) => {
              e.stopPropagation();
              const workItem = (row.FormDueItems || [])[0];
              if (!workItem) return;
              setFormFillBusy(true);
              try {
                let instanceId =
                  workItem.FormInstanceId || workItem.formInstanceId || null;
                if (!instanceId && phase === "fill") {
                  const templateId =
                    workItem.FormTemplateId || workItem.formTemplateId;
                  const occurrenceId =
                    workItem.FormOccurrenceId || workItem.formOccurrenceId;
                  const created = await createInstance({
                    FormTemplateId: templateId,
                    StudyId: ctx.studyId,
                    SiteId: ctx.siteId,
                    Title: workItem.Title || workItem.title || "Pharmacy form",
                  });
                  if (!created.success || !created.formInstanceId) return;
                  instanceId = created.formInstanceId;
                  await linkFormInstance({
                    FormOccurrenceId: occurrenceId,
                    FormInstanceId: instanceId,
                  });
                  refreshFormDues();
                }
                if (!instanceId) return;
                setFormFillModal({
                  formInstanceId: instanceId,
                  workItem: {
                    ...workItem,
                    FormInstanceId: instanceId,
                    LinkedTxnId:
                      workItem.LinkedTxnId ||
                      workItem.linkedTxnId ||
                      null,
                    StudyId: workItem.StudyId || ctx.studyId,
                    SiteId: workItem.SiteId || ctx.siteId,
                  },
                  mode:
                    phase === "approve"
                      ? "review"
                      : phase === "locked"
                        ? "locked"
                        : "fill",
                });
              } finally {
                setFormFillBusy(false);
              }
            }}
          >
            {label}
          </button>
        );
      }
      if (field === "Quantity") {
        return <ImpQtyKitFloatCell row={row} />;
      }
      if (field === "ReceiptStatus") {
        const status = row.ReceiptStatus || row.receiptStatus;
        if (String(status) === "DestroyRequested") {
          const canConfirm =
            row.FormDuePhase === "locked" || row.FormDuePhase === "none";
          return (
            <span className="d-inline-flex flex-column gap-1 align-items-start">
              {renderReceiptStatusBadge("DestroyRequested")}
              {canConfirm ? (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  disabled={busy}
                  onClick={async (e) => {
                    e.stopPropagation();
                    setBusy(true);
                    try {
                      const result = await confirmDestroyRequest({
                        StudyId: ctx.studyId,
                        SiteId: ctx.siteId,
                        ImpTxnId: row.ImpTxnId || row.impTxnId,
                      });
                      if (result.success) {
                        await refresh();
                        refreshFormDues();
                      } else {
                        setError(result.message || "Confirm destruction failed.");
                      }
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Confirm destruction
                </button>
              ) : null}
            </span>
          );
        }
        return status ? renderReceiptStatusBadge(status) : "—";
      }
      if (field === "TxnAt") {
        if (row._pendingDestroy) {
          return (
            <PendingDestroyActionCell
              row={row}
              onComplete={openDestroyFromPending}
              busy={busy}
            />
          );
        }
        return renderImpMovementCell(row, field);
      }
      if (row._pendingDestroy && field === "ReferenceNo") return "—";
      if (row._pendingDestroy && field === "CreatedByName") return "—";
      return renderImpMovementCell(row, field);
    },
    [
      busy,
      ctx.siteId,
      ctx.studyId,
      formFillBusy,
      openDestroyFromPending,
      refresh,
      refreshFormDues,
      setError,
    ],
  );

  const getUnifiedRowKey = useCallback((row) => {
    if (row?._pendingDestroy) {
      return `pending-destroy-${row.ImpTxnId || row.impTxnId}`;
    }
    return getImpTxnRowKey(row);
  }, []);

  const openAdd = () => {
    setDestroyFromPending(false);
    setForm(emptyForm());
    setEvidenceSlots(emptyEvidenceSlots());
    setKits([]);
    setDestroyKitSearch("");
    setCollapsedKitTreeBatches(new Set());
    setFormError("");
    setError("");
    setFormBlocked(false);
    setFormBlockMessage("");
    setNeedsRequest(false);
    setShowModal(true);
  };

  const closeModal = () => {
    if (busy || requestBusy) return;
    setShowModal(false);
    setDestroyFromPending(false);
    setEvidenceSlots(emptyEvidenceSlots());
    setDestroyKitSearch("");
    setCollapsedKitTreeBatches(new Set());
    setFormError("");
    setFormBlocked(false);
    setFormBlockMessage("");
    setNeedsRequest(false);
    if (overlay) onOverlayClose?.();
  };

  const refreshDestroyFormGate = useCallback(async () => {
    if (!ctx.studyId || !ctx.siteId) {
      setFormBlocked(false);
      setFormBlockMessage("");
      setNeedsRequest(false);
      return;
    }
    const linkedTxnId =
      String(form.sourceTxnId || preferSourceId || "").trim() || null;
    setFormGateBusy(true);
    try {
      /* Already In destruction: ensure form due on the held source — do not ask Create request again. */
      if (destroyFromPending && linkedTxnId) {
        const key = linkedTxnId.toLowerCase();
        if (!ensuredDestroyFormTxnRef.current.has(key)) {
          try {
            await ensureFormOccurrence({
              StudyId: ctx.studyId,
              SiteId: ctx.siteId,
              TriggerCode: "DESTRUCTION_REQUESTED",
              LinkedTxnId: linkedTxnId,
            });
          } catch {
            /* non-fatal */
          } finally {
            ensuredDestroyFormTxnRef.current.add(key);
          }
          refreshFormDues();
        }
      }

      const res = await checkFormActionBlockers(
        ctx.studyId,
        ctx.siteId,
        "CONFIRM_DESTRUCTION",
        linkedTxnId,
      );
      const needReq = formBlockersNeedRequest(res);

      if (destroyFromPending) {
        /* Pending path never uses Create request — form should already be due. */
        const message = formBlockMessageForPendingDestroy(
          res,
          "Confirm destruction",
        );
        if (res.success && message) {
          setFormBlocked(true);
          setFormBlockMessage(message);
          setNeedsRequest(false);
        } else if (!res.success) {
          setFormBlocked(true);
          setFormBlockMessage(
            res.message ||
              "Could not verify destruction form requirements. Confirm destruction is blocked until the check succeeds.",
          );
          setNeedsRequest(false);
        } else {
          setFormBlocked(false);
          setFormBlockMessage("");
          setNeedsRequest(false);
        }
        return;
      }

      const message = formBlockMessageForAction(res, "Confirm destruction");
      if (res.success && message) {
        setFormBlocked(true);
        setFormBlockMessage(message);
        setNeedsRequest(needReq);
      } else if (!res.success) {
        setFormBlocked(true);
        setFormBlockMessage(
          res.message ||
            "Could not verify destruction form requirements. Confirm destruction is blocked until the check succeeds.",
        );
        setNeedsRequest(false);
      } else {
        setFormBlocked(false);
        setFormBlockMessage("");
        setNeedsRequest(false);
      }
    } catch {
      setFormBlocked(true);
      setFormBlockMessage(
        "Could not verify destruction form requirements. Confirm destruction is blocked until the check succeeds.",
      );
      setNeedsRequest(false);
    } finally {
      setFormGateBusy(false);
    }
  }, [
    ctx.studyId,
    ctx.siteId,
    form.sourceTxnId,
    preferSourceId,
    destroyFromPending,
    refreshFormDues,
  ]);

  /* Gate Post only — Create request (or DestructionPending release) creates the due form. */
  useEffect(() => {
    if (!showModal) {
      setFormBlocked(false);
      setFormBlockMessage("");
      setNeedsRequest(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      await refreshDestroyFormGate();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [showModal, refreshDestroyFormGate]);

  const onCreateRequest = async () => {
    setFormError("");
    if (!canDestroy && !canView) {
      setFormError("You do not have permission to destroy IMP.");
      return;
    }
    if (!ctx.studyId || !ctx.siteId || !form.impBatchId) {
      setFormError("Select study, site and batch.");
      return;
    }
    if (isIndividualKit && !form.selectedKitIds.length) {
      setFormError("Select kits to destroy. Empty selection is not allowed.");
      return;
    }
    const qty = isIndividualKit
      ? form.selectedKitIds.length
      : Number(form.quantity || autoDestroyQty || 0);
    if (!(qty > 0)) {
      setFormError("Quantity is required.");
      return;
    }
    setRequestBusy(true);
    try {
      const result = await createDestroyRequest({
        StudyId: ctx.studyId,
        SiteId: ctx.siteId,
        ImpBatchId: form.impBatchId,
        Quantity: qty,
        TxnAt: toApiDateTimeString(form.txnDate, form.txnTime),
        ReferenceNo: form.referenceNo.trim(),
        Notes: [
          form.reason ? `Reason: ${form.reason}` : null,
          form.method ? `Method: ${form.method}` : null,
        ]
          .filter(Boolean)
          .join("\n"),
        SourceTxnId: form.sourceTxnId || preferSourceId || null,
        KitIdsJson: isIndividualKit
          ? JSON.stringify(form.selectedKitIds)
          : null,
        ClientRequestId:
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `des-${Date.now()}`,
      });
      if (!result.success) {
        setFormError(result.message || "Could not create destruction request.");
        return;
      }
      setShowModal(false);
      setForm(emptyForm());
      await refresh();
      refreshFormDues();
      showAlert({
        title: "Destruction request created",
        message:
          "The request is on the Destroy list. Completion-role staff were emailed. Use Fill form, then Confirm destruction after approval.",
        type: "success",
        buttonText: "OK",
      });
      if (overlay) onOverlaySuccess?.();
    } finally {
      setRequestBusy(false);
    }
  };

  useEffect(() => {
    if (!overlay) return undefined;
    setDestroyFromPending(Boolean(preferSourceId));
    setForm({
      ...emptyForm(),
      impBatchId: preferBatchId || "",
      sourceTxnId: preferSourceId || "",
    });
    setEvidenceSlots(emptyEvidenceSlots());
    setKits([]);
    setFormError("");
    setError("");
    setFormBlocked(false);
    setFormBlockMessage("");
    setNeedsRequest(false);
    setShowModal(true);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay]);

  useEffect(() => {
    if (!overlay || !showModal) return;
    if (!batchOptions.length) return;

    setForm((prev) => {
      let next = { ...prev };
      let changed = false;

      if (!next.impBatchId) {
        const preferred =
          preferBatchId &&
          batchOptions.some((o) => o.value === preferBatchId)
            ? preferBatchId
            : batchOptions[0].value;
        if (preferred) {
          next.impBatchId = preferred;
          changed = true;
        }
      }

      if (preferSourceId && !next.sourceTxnId) {
        next.sourceTxnId = preferSourceId;
        changed = true;
      }

      const row =
        batchOptions.find((o) => o.value === next.impBatchId)?.row || null;
      const tracking = String(
        row?.InventoryTrackingMethod || row?.inventoryTrackingMethod || "",
      );
      if (tracking !== "IndividualKit" && !next.quantity && row) {
        const heldQty = batchHeld(row);
        const qty = heldQty > 0 ? heldQty : batchOnHand(row);
        if (qty > 0) {
          next.quantity = String(qty);
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [overlay, showModal, batchOptions, preferBatchId, preferSourceId]);

  const toggleKitTreeBatch = (batchKey) => {
    const key = String(batchKey || "");
    if (!key) return;
    setCollapsedKitTreeBatches((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleDestroyKit = (kitId) => {
    const id = String(kitId || "");
    if (!id) return;
    const kitMeta = destroyKitGroups
      .flatMap((g) => g.kits || [])
      .find((k) => String(k.ImpKitId || k.impKitId) === id);
    const kitBatchId = String(kitMeta?.ImpBatchId || "");
    const receiptId = String(kitMeta?.ReceiptTxnId || "").trim();
    const kitStatus = String(kitMeta?.Status || kitMeta?.status || "");

    setForm((prev) => {
      const switchingBatch =
        kitBatchId &&
        prev.impBatchId &&
        String(prev.impBatchId) !== kitBatchId;
      const set = new Set(switchingBatch ? [] : prev.selectedKitIds);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      const ids = Array.from(set);
      let sourceTxnId = prev.sourceTxnId;
      if (switchingBatch && !destroyFromPending) {
        sourceTxnId = "";
      }
      if (
        !destroyFromPending &&
        ids.includes(id) &&
        kitStatus === "DestructionPending" &&
        receiptId &&
        (!sourceTxnId || switchingBatch)
      ) {
        sourceTxnId = receiptId;
      }
      if (!ids.length && !destroyFromPending) {
        sourceTxnId = "";
      }
      return {
        ...prev,
        impBatchId: ids.length
          ? kitBatchId || prev.impBatchId
          : destroyFromPending
            ? prev.impBatchId
            : "",
        sourceTxnId: destroyFromPending ? prev.sourceTxnId : sourceTxnId,
        selectedKitIds: ids,
        quantity: ids.length ? String(ids.length) : "",
      };
    });
  };

  const selectDestroyBatchLine = (batchId) => {
    const id = String(batchId || "");
    const row = batchOptions.find((o) => o.value === id)?.row || null;
    const heldQty = row ? batchHeld(row) : 0;
    const onHandQty = row ? batchOnHand(row) : 0;
    const qty = heldQty > 0 ? heldQty : onHandQty;
    setForm((prev) => ({
      ...prev,
      impBatchId: id,
      sourceTxnId: destroyFromPending ? prev.sourceTxnId : "",
      selectedKitIds: [],
      quantity: qty > 0 ? String(qty) : "",
    }));
  };

  const toggleDestroyBatchAllKits = (group) => {
    if (!group || group.type !== "kits") return;
    const batchId = String(group.batchId || "");
    const kitIds = (group.kits || [])
      .map((k) => String(k.ImpKitId || k.impKitId || ""))
      .filter(Boolean);
    if (!batchId || !kitIds.length) return;

    setCollapsedKitTreeBatches((prev) => {
      const next = new Set(prev);
      next.delete(batchId);
      return next;
    });

    setForm((prev) => {
      const allSelected =
        kitIds.length > 0 &&
        kitIds.every((kid) => prev.selectedKitIds.includes(kid));
      if (allSelected) {
        return {
          ...prev,
          selectedKitIds: [],
          quantity: "",
          impBatchId: destroyFromPending ? prev.impBatchId : "",
          sourceTxnId: destroyFromPending ? prev.sourceTxnId : "",
        };
      }
      const firstPending = (group.kits || []).find(
        (k) =>
          String(k.Status || k.status || "") === "DestructionPending" &&
          (k.ReceiptTxnId || k.receiptTxnId),
      );
      const receiptId = String(
        firstPending?.ReceiptTxnId || firstPending?.receiptTxnId || "",
      ).trim();
      return {
        ...prev,
        impBatchId: batchId,
        sourceTxnId: destroyFromPending
          ? prev.sourceTxnId
          : String(prev.impBatchId) === batchId
            ? prev.sourceTxnId || receiptId
            : receiptId,
        selectedKitIds: kitIds,
        quantity: String(kitIds.length),
      };
    });
  };

  const onEvidenceSlotPick = (slotIndex, ev) => {
    const file = (ev.target.files && ev.target.files[0]) || null;
    ev.target.value = "";
    if (!file) return;
    if (!isAllowedDestroyEvidenceFile(file)) {
      setFormError("Evidence must be an image or PDF.");
      return;
    }
    setFormError("");
    setEvidenceSlots((prev) => {
      const next = [...prev];
      next[slotIndex] = file;
      return next;
    });
  };

  const clearEvidenceSlot = (slotIndex) => {
    setEvidenceSlots((prev) => {
      const next = [...prev];
      next[slotIndex] = null;
      return next;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setError("");
    if (!canDestroy && !canView) {
      setFormError("You do not have permission to destroy IMP.");
      return;
    }
    if (formBlocked || formGateBusy) {
      setFormError(
        formBlockMessage ||
          "Confirm destruction is blocked until the required form is completed and approved.",
      );
      return;
    }
    if (!ctx.studyId || !ctx.siteId || !form.impBatchId) {
      setFormError("Select study, site and batch.");
      return;
    }
    if (!String(form.reason || "").trim()) {
      setFormError("Destruction reason is required.");
      return;
    }
    if (!String(form.method || "").trim()) {
      setFormError("Destruction method is required.");
      return;
    }
    if (isIndividualKit) {
      if (!form.selectedKitIds.length) {
        setFormError("Select kits to destroy. Empty selection is not allowed.");
        return;
      }
    } else if (!(autoDestroyQty > 0)) {
      setFormError("No destroyable quantity found for this batch.");
      return;
    }

    const qty = autoDestroyQty;
    const countLabel = isIndividualKit
      ? `${qty} kit${qty === 1 ? "" : "s"}`
      : `quantity ${qty}`;

    showAlert({
      title: "Confirm destruction",
      message: `Are you sure you want to post destruction for ${countLabel}? You must authenticate to complete the final submission.`,
      type: "warning",
      buttonText: "Continue",
      cancelText: "Cancel",
      onConfirm: async () => {
        hideAlert();
        const sign = await openESignatureConfirmModal({
          title: "Sign destruction",
        });
        if (
          !sign ||
          sign.cancelled ||
          Number(sign.Success ?? sign.success ?? 0) !== 1 ||
          !sign.SignatureBase64
        ) {
          return;
        }

        const auth = await openAuthModal({
          introMessage:
            "Re-authenticate to post this IMP destruction. Add destruction notes if needed.",
          showDescription: true,
          requireDescription: false,
          descriptionLabel: "Destruction notes",
          descriptionPlaceholder: "Optional notes for this destruction…",
          descriptionMaxLength: 1000,
        });
        if (
          !auth ||
          auth.cancelled ||
          Number(auth.Success ?? auth.success ?? 0) !== 1
        ) {
          return;
        }
        const notes = String(auth.Description || auth.description || "").trim();
        await postDestroyAfterAuth(notes, qty);
      },
    });
  };

  const postDestroyAfterAuth = async (destroyNotes, qty) => {
    setBusy(true);
    setFormError("");
    try {
      const methodLabel =
        DESTROY_METHOD_OPTIONS.find((o) => o.value === form.method)?.label ||
        form.method;
      const reasonLabel =
        DESTROY_REASON_OPTIONS.find((o) => o.value === form.reason)?.label ||
        form.reason;
      const notesParts = [
        `Reason: ${reasonLabel}`,
        `Method: ${methodLabel}`,
        destroyNotes || null,
      ].filter(Boolean);

      const result = await postImpDestroy({
        StudyId: ctx.studyId,
        SiteId: ctx.siteId,
        ImpBatchId: form.impBatchId,
        Quantity: qty,
        TxnAt: toApiDateTimeString(form.txnDate, form.txnTime),
        ReferenceNo: form.referenceNo.trim(),
        Notes: notesParts.join("\n"),
        SourceTxnId: form.sourceTxnId || preferSourceId || null,
        KitIdsJson: isIndividualKit
          ? JSON.stringify(form.selectedKitIds)
          : null,
      });
      if (!result.success) {
        setFormError(result.message || "Destruction failed.");
        return;
      }

      const destroyTxnId = String(
        result.rows?.[0]?.ImpTxnId ||
          result.rows?.[0]?.impTxnId ||
          result.response?.data?.Data?.[0]?.ImpTxnId ||
          "",
      ).trim();

      const evidenceFiles = (evidenceSlots || []).filter(Boolean);
      if (destroyTxnId && evidenceFiles.length) {
        for (const file of evidenceFiles) {
          const up = await uploadReceiptEvidence({
            studyId: ctx.studyId,
            siteId: ctx.siteId,
            impTxnId: destroyTxnId,
            evidenceType: evidenceTypeForFile(file),
            notes: "",
            file,
            evidenceScope: "Destroy",
          });
          if (!up.success) {
            setFormError(
              up.message ||
                "Destruction was recorded, but one evidence file failed to upload.",
            );
            await refresh();
            return;
          }
        }
      }

      setShowModal(false);
      setDestroyFromPending(false);
      setForm(emptyForm());
      setEvidenceSlots(emptyEvidenceSlots());
      await refresh();
      if (overlay) onOverlaySuccess?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <EpsfAccessGate allow={allow} loading={loading}>
      {!overlay ? (
        <div className="epsf-list-page">
          <EpsfPageBreadcrumb
            items={[
              { label: "ePSF", path: "/epsf/imp/inventory" },
              { label: "IMP" },
              { label: "Destruction" },
            ]}
            actions={[
              {
                icon: "fas fa-refresh",
                label: "Refresh",
                onClick: () => refresh(),
              },
              {
                icon: "fas fa-fire",
                label: "Record destruction",
                onClick: openAdd,
              },
            ]}
          />

          {error ? <p className="epsf-list-page__error">{error}</p> : null}

          {ctx.studyId && ctx.siteId ? (
            <div className="epsf-return-summary" aria-label="Destruction summary">
              <div className="epsf-return-summary__item epsf-return-summary__item--pending">
                <span className="epsf-return-summary__label">
                  Awaiting destruction
                </span>
                <span className="epsf-return-summary__value">
                  {destroySummary.pending}
                </span>
              </div>
              <div className="epsf-return-summary__item epsf-return-summary__item--stock">
                <span className="epsf-return-summary__label">
                  Destroyed posted
                </span>
                <span className="epsf-return-summary__value">
                  {destroySummary.destroyed}
                </span>
              </div>
              <div className="epsf-return-summary__item epsf-return-summary__item--participant">
                <span className="epsf-return-summary__label">
                  Kits / qty destroyed
                </span>
                <span className="epsf-return-summary__value">
                  {destroySummary.kitsDestroyed}
                </span>
              </div>
              <div
                className={`epsf-return-summary__item epsf-return-summary__item--trend ${trendTone(
                  weeklyTrend.completedPct,
                )}`}
                title={`Destroyed vs last week · this week ${weeklyTrend.thisWeek} · last week ${weeklyTrend.lastWeek}`}
              >
                <span className="epsf-return-summary__label">
                  Destroyed vs last week
                </span>
                <i
                  className={`fas ${trendIconClass(weeklyTrend.completedPct)} epsf-return-summary__trend-icon`}
                  aria-hidden="true"
                />
                <span className="epsf-return-summary__value epsf-return-summary__value--trend">
                  {formatPctChange(weeklyTrend.completedPct)}
                </span>
              </div>
            </div>
          ) : null}

          <div className="epsf-report-wrap">
            <ReportTable
              rows={destroyTableRows}
              loading={listLoading}
              fieldOptions={DESTROY_FIELD_OPTIONS}
              defaultColumns={DESTROY_DEFAULT_COLUMNS}
              getFieldLabel={(field) =>
                DESTROY_FIELD_OPTIONS.find((x) => x.key === field)?.label ||
                field
              }
              renderCell={renderCell}
              getRowKey={getUnifiedRowKey}
              persistLayoutKey="epsf-imp-destroy-txns-v8"
              emptyMessage={
                !ctx.studyId || !ctx.siteId
                  ? "Select study and site in the breadcrumb bar."
                  : "No destruction records for this study and site yet."
              }
              pageSizeOptions={[10, 25, 50, 100]}
              columnSearchMode="client"
            />
          </div>
        </div>
      ) : null}

      {showModal ? (
        <div
          className="modal fade show d-block epsf-modal epsf-return-modal"
          tabIndex="-1"
          aria-modal="true"
        >
          <div className="modal-dialog modal-xl epsf-return-modal__dialog">
            <div className="modal-content epsf-return-modal__content">
              <form
                onSubmit={onSubmit}
                className="epsf-return-modal__form"
              >
                <div className="modal-header py-2">
                  <h5 className="modal-title mb-0">Record destruction</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={closeModal}
                    disabled={busy}
                  />
                </div>
                <div className="modal-body epsf-return-modal__body epsf-return-modal__body--split">
                  <div className="row g-3 epsf-return-stock-layout">
                    <div className="col-12 col-lg-7 epsf-return-stock-layout__form">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <div className="epsf-form-field">
                            <label className="form-label">
                              Reason <span className="mandatory">*</span>
                            </label>
                            <Dropdown
                              options={DESTROY_REASON_OPTIONS}
                              selected={findOption(
                                DESTROY_REASON_OPTIONS,
                                form.reason,
                              )}
                              onSelect={(opt) =>
                                setForm((prev) => ({
                                  ...prev,
                                  reason: opt?.value || "",
                                }))
                              }
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="epsf-form-field">
                            <label className="form-label">
                              Method <span className="mandatory">*</span>
                            </label>
                            <Dropdown
                              options={DESTROY_METHOD_OPTIONS}
                              selected={findOption(
                                DESTROY_METHOD_OPTIONS,
                                form.method,
                              )}
                              onSelect={(opt) =>
                                setForm((prev) => ({
                                  ...prev,
                                  method: opt?.value || "",
                                }))
                              }
                            />
                          </div>
                        </div>

                        {destroyFromPending ? (
                          <div className="col-12">
                            <div className="epsf-form-field">
                              <label className="form-label">
                                Destroy source{" "}
                                <span className="mandatory">*</span>
                              </label>
                              <input
                                className="form-control"
                                readOnly
                                value={pendingSourceSummary}
                                title={pendingSourceSummary}
                              />
                            </div>
                          </div>
                        ) : null}

                        {form.impBatchId || selectedDestroyKitBatchSummary ? (
                          <div className="col-12">
                            <div className="epsf-return-summary-card">
                              <div className="epsf-return-summary-card__row">
                                <div>
                                  <span className="epsf-return-summary-card__label">
                                    IMP / Batch
                                  </span>
                                  <div className="epsf-return-summary-card__value">
                                    {selectedDestroyKitBatchSummary
                                      ? `${selectedDestroyKitBatchSummary.productCode || "—"} / ${
                                          selectedDestroyKitBatchSummary.batchNumber ||
                                          "—"
                                        }`
                                      : "Pick stock on the right"}
                                  </div>
                                </div>
                                <div>
                                  <span className="epsf-return-summary-card__label">
                                    Source
                                  </span>
                                  <div className="epsf-return-summary-card__value">
                                    {form.sourceTxnId
                                      ? "Destruction pending"
                                      : "Available / on-hand"}
                                  </div>
                                </div>
                              </div>
                              {selectedDestroyKitBatchSummary ? (
                                <div className="epsf-return-summary-card__stats">
                                  <div>
                                    <span>On hand</span>
                                    <strong>
                                      {selectedDestroyKitBatchSummary.onHand}
                                    </strong>
                                  </div>
                                  <div>
                                    <span>Held</span>
                                    <strong>
                                      {selectedDestroyKitBatchSummary.held}
                                    </strong>
                                  </div>
                                  {isIndividualKit ? (
                                    <div>
                                      <span>Selected kits</span>
                                      <strong>
                                        {
                                          selectedDestroyKitBatchSummary.selectedCount
                                        }
                                      </strong>
                                    </div>
                                  ) : (
                                    <div>
                                      <span>Destroy qty</span>
                                      <strong>
                                        {form.quantity || autoDestroyQty || "—"}
                                      </strong>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="epsf-return-summary-card__hint mb-0">
                                  Select kits or a batch line on the right to
                                  fill IMP, batch and quantities.
                                </p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="col-12">
                            <div className="epsf-return-summary-card">
                              <p className="epsf-return-summary-card__hint mb-0">
                                Select kits or a batch line on the right to fill
                                IMP, batch and quantities.
                              </p>
                            </div>
                          </div>
                        )}

                        <div className="col-md-6">
                          <div className="epsf-form-field">
                            <label className="form-label">
                              Certificate / Reference
                            </label>
                            <input
                              className="form-control"
                              value={form.referenceNo}
                              onChange={(ev) =>
                                setForm((prev) => ({
                                  ...prev,
                                  referenceNo: ev.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="epsf-form-field">
                            <label className="form-label">
                              Destruction date &amp; time{" "}
                              <span className="mandatory">*</span>
                            </label>
                            <div className="epsf-receipt-datetime-pair">
                              <SegmentDatePicker
                                selected={form.txnDate}
                                onChange={(d) =>
                                  setForm((prev) => ({ ...prev, txnDate: d }))
                                }
                              />
                              <SegmentTimePicker
                                value={form.txnTime}
                                onChange={(t) =>
                                  setForm((prev) => ({ ...prev, txnTime: t }))
                                }
                              />
                            </div>
                          </div>
                        </div>

                        <div className="col-12">
                          <div className="epsf-form-field">
                            <div className="epsf-destroy-evidence-head">
                              <label className="form-label mb-0">
                                Evidence{" "}
                                <span className="epsf-destroy-evidence-head__sub">
                                  (optional · image or PDF)
                                </span>
                              </label>
                              <span
                                className="epsf-destroy-evidence-head__count"
                                aria-live="polite"
                              >
                                {
                                  (evidenceSlots || []).filter(Boolean)
                                    .length
                                }
                                /{MAX_DESTROY_EVIDENCE}
                              </span>
                            </div>
                            <div
                              className="epsf-destroy-evidence-tiles"
                              role="group"
                              aria-label="Destruction evidence uploads"
                            >
                              {evidenceSlots.map((file, idx) => {
                                const inputId = `epsf-destroy-evidence-${idx}`;
                                const filled = Boolean(file);
                                const previewUrl = evidencePreviewUrls[idx];
                                const isPdf = filled && isPdfEvidenceFile(file);
                                return (
                                  <div
                                    key={inputId}
                                    className={`epsf-destroy-evidence-tile${
                                      filled
                                        ? " epsf-destroy-evidence-tile--filled"
                                        : ""
                                    }`}
                                  >
                                    <input
                                      id={inputId}
                                      className="epsf-destroy-evidence-tile__input"
                                      type="file"
                                      accept="image/*,application/pdf,.pdf"
                                      disabled={busy}
                                      onChange={(ev) =>
                                        onEvidenceSlotPick(idx, ev)
                                      }
                                    />
                                    {filled ? (
                                      <>
                                        <label
                                          htmlFor={inputId}
                                          className="epsf-destroy-evidence-tile__body epsf-destroy-evidence-tile__body--preview"
                                        >
                                          <span className="epsf-destroy-evidence-tile__preview">
                                            {previewUrl &&
                                            isImageEvidenceFile(file) ? (
                                              <img
                                                src={previewUrl}
                                                alt=""
                                                className="epsf-destroy-evidence-tile__thumb"
                                              />
                                            ) : previewUrl && isPdf ? (
                                              <EvidencePdfThumb
                                                fileUrl={previewUrl}
                                              />
                                            ) : (
                                              <span
                                                className={`epsf-destroy-evidence-tile__doc${
                                                  isPdf
                                                    ? " epsf-destroy-evidence-tile__doc--pdf"
                                                    : ""
                                                }`}
                                              >
                                                {isPdf ? "PDF" : "FILE"}
                                              </span>
                                            )}
                                          </span>
                                          <span className="epsf-destroy-evidence-tile__caption">
                                            <span className="epsf-destroy-evidence-tile__name">
                                              {file.name}
                                            </span>
                                            <span className="epsf-destroy-evidence-tile__hint">
                                              Click to replace
                                            </span>
                                          </span>
                                        </label>
                                        <button
                                          type="button"
                                          className="epsf-destroy-evidence-tile__remove"
                                          onClick={(ev) => {
                                            ev.preventDefault();
                                            ev.stopPropagation();
                                            clearEvidenceSlot(idx);
                                          }}
                                          disabled={busy}
                                          aria-label={`Remove ${file.name || `evidence ${idx + 1}`}`}
                                          title="Remove"
                                        >
                                          ×
                                        </button>
                                      </>
                                    ) : (
                                      <label
                                        htmlFor={inputId}
                                        className="epsf-destroy-evidence-tile__body"
                                      >
                                        <span
                                          className="epsf-destroy-evidence-tile__plus"
                                          aria-hidden="true"
                                        >
                                          +
                                        </span>
                                        <span className="epsf-destroy-evidence-tile__title">
                                          File {idx + 1}
                                        </span>
                                        <span className="epsf-destroy-evidence-tile__hint">
                                          Click to upload
                                        </span>
                                      </label>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-lg-5 epsf-return-stock-layout__track">
                      <aside
                        className="epsf-return-kits-panel"
                        aria-label="Destroyable stock kits"
                      >
                        <div className="epsf-return-kits-panel__head">
                          <h6 className="epsf-return-kits-panel__title">
                            {destroyFromPending
                              ? "Destruction-pending kits"
                              : "Available stock"}
                          </h6>
                          {isIndividualKit && form.impBatchId ? (
                            <span className="epsf-return-kits-panel__count">
                              {form.selectedKitIds.length} /{" "}
                              {eligibleKits.length} selected
                            </span>
                          ) : null}
                        </div>

                        <div className="epsf-return-kits-panel__search">
                          <i
                            className="fas fa-search epsf-return-kits-panel__search-icon"
                            aria-hidden="true"
                          />
                          <input
                            type="search"
                            className="epsf-return-kits-panel__search-input"
                            placeholder="Search batch or kit…"
                            value={destroyKitSearch}
                            onChange={(e) =>
                              setDestroyKitSearch(e.target.value)
                            }
                            aria-label="Search batch or kit"
                          />
                        </div>

                        {form.sourceTxnId && !stockTrackBusy ? (
                          <dl className="epsf-return-track-panel__meta mb-2 px-2">
                            <div>
                              <dt>Initiated by</dt>
                              <dd>{stockInitiatedBy || "—"}</dd>
                            </div>
                            <div>
                              <dt>Initiated at</dt>
                              <dd>{stockInitiatedAt || "—"}</dd>
                            </div>
                            {stockReviewComment ? (
                              <div>
                                <dt>Review comment</dt>
                                <dd>{stockReviewComment}</dd>
                              </div>
                            ) : null}
                          </dl>
                        ) : null}

                        {filteredDestroyKitGroups.length ? (
                          <div
                            className="epsf-return-kits-panel__list epsf-return-kits-tree"
                            role="tree"
                            aria-label="Batches and kits"
                          >
                            {filteredDestroyKitGroups.map((group) => {
                              const treeKey = String(group.batchId || "");
                              const open =
                                !collapsedKitTreeBatches.has(treeKey);
                              const batchTitle =
                                [group.productCode, group.batchNumber]
                                  .filter(Boolean)
                                  .join(" · ") || "Batch";
                              const kitIds =
                                group.type === "kits"
                                  ? (group.kits || [])
                                      .map((k) =>
                                        String(k.ImpKitId || k.impKitId || ""),
                                      )
                                      .filter(Boolean)
                                  : [];
                              const selectedInBatch = kitIds.filter((id) =>
                                form.selectedKitIds.includes(id),
                              );
                              const allSelected =
                                kitIds.length > 0 &&
                                selectedInBatch.length === kitIds.length;
                              const someSelected =
                                selectedInBatch.length > 0 && !allSelected;
                              const countLabel =
                                group.type === "kits"
                                  ? `${group.kits.length} kit${
                                      group.kits.length === 1 ? "" : "s"
                                    }`
                                  : `On hand ${group.onHand}`;
                              return (
                                <div
                                  key={treeKey}
                                  className={`epsf-return-kits-tree__node${
                                    open
                                      ? " epsf-return-kits-tree__node--open"
                                      : ""
                                  }`}
                                  role="treeitem"
                                  aria-expanded={open}
                                >
                                  <div className="epsf-return-kits-tree__batch">
                                    <button
                                      type="button"
                                      className="epsf-return-kits-tree__toggle"
                                      onClick={() =>
                                        toggleKitTreeBatch(treeKey)
                                      }
                                      aria-label={`${
                                        open ? "Collapse" : "Expand"
                                      } ${batchTitle}`}
                                    >
                                      <i
                                        className={`fas fa-chevron-${
                                          open ? "down" : "right"
                                        }`}
                                        aria-hidden="true"
                                      />
                                    </button>
                                    {group.type === "kits" ? (
                                      <input
                                        type="checkbox"
                                        className="epsf-return-kits-tree__batch-check"
                                        checked={allSelected}
                                        ref={(el) => {
                                          if (el)
                                            el.indeterminate = someSelected;
                                        }}
                                        onChange={() =>
                                          toggleDestroyBatchAllKits(group)
                                        }
                                        aria-label={`Select all kits in ${batchTitle}`}
                                        title="Select all kits in this batch"
                                      />
                                    ) : (
                                      <input
                                        type="checkbox"
                                        className="epsf-return-kits-tree__batch-check"
                                        checked={
                                          form.impBatchId === group.batchId &&
                                          !form.selectedKitIds.length
                                        }
                                        onChange={() => {
                                          if (
                                            form.impBatchId === group.batchId &&
                                            !form.selectedKitIds.length
                                          ) {
                                            setForm((prev) => ({
                                              ...prev,
                                              impBatchId: destroyFromPending
                                                ? prev.impBatchId
                                                : "",
                                              quantity: "",
                                            }));
                                          } else {
                                            selectDestroyBatchLine(
                                              group.batchId,
                                            );
                                          }
                                        }}
                                        aria-label={`Select batch ${batchTitle}`}
                                      />
                                    )}
                                    <button
                                      type="button"
                                      className="epsf-return-kits-tree__batch-main"
                                      onClick={() =>
                                        toggleKitTreeBatch(treeKey)
                                      }
                                    >
                                      <span className="epsf-return-kits-tree__batch-name">
                                        {batchTitle}
                                      </span>
                                      <span className="epsf-return-kits-tree__batch-count">
                                        {countLabel}
                                      </span>
                                    </button>
                                  </div>
                                  {open ? (
                                    group.type === "kits" ? (
                                      <ul
                                        className="epsf-return-kits-tree__children"
                                        role="group"
                                      >
                                        {group.kits.map((k) => {
                                          const id = String(
                                            k.ImpKitId || k.impKitId || "",
                                          );
                                          const checked =
                                            form.selectedKitIds.includes(id);
                                          const status =
                                            k.Status || k.status || "";
                                          return (
                                            <li key={id} role="treeitem">
                                              <label
                                                className={`epsf-return-kits-panel__item epsf-return-kits-panel__item--flat${
                                                  checked
                                                    ? " epsf-return-kits-panel__item--selected"
                                                    : ""
                                                }`}
                                              >
                                                <input
                                                  type="checkbox"
                                                  checked={checked}
                                                  onChange={() =>
                                                    toggleDestroyKit(id)
                                                  }
                                                />
                                                <span className="epsf-return-kits-panel__kit-no">
                                                  {k.KitNumber ||
                                                    k.kitNumber ||
                                                    id}
                                                </span>
                                                {status
                                                  ? renderReceiptStatusBadge(
                                                      status,
                                                    )
                                                  : null}
                                              </label>
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    ) : (
                                      <ul
                                        className="epsf-return-kits-tree__children"
                                        role="group"
                                      >
                                        <li role="treeitem">
                                          <button
                                            type="button"
                                            className={`epsf-return-kits-panel__item epsf-return-kits-panel__item--flat epsf-return-kits-panel__item--btn${
                                              form.impBatchId ===
                                                group.batchId &&
                                              !form.selectedKitIds.length
                                                ? " epsf-return-kits-panel__item--selected"
                                                : ""
                                            }`}
                                            onClick={() =>
                                              selectDestroyBatchLine(
                                                group.batchId,
                                              )
                                            }
                                          >
                                            <span className="epsf-return-kits-panel__kit-no">
                                              Batch quantity destruction
                                            </span>
                                            <span className="epsf-return-kits-panel__meta">
                                              On hand {group.onHand}
                                            </span>
                                          </button>
                                        </li>
                                      </ul>
                                    )
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="epsf-return-kits-panel__empty">
                            {destroyKitSearch.trim()
                              ? "No batches or kits match this search."
                              : destroyFromPending
                                ? "No destruction-pending kits on this source."
                                : "No destroyable batches or kits for this study / site."}
                          </p>
                        )}
                      </aside>
                    </div>
                  </div>
                  {formBlockMessage ? (
                    <p className="epsf-list-page__error mt-2 mb-0">
                      {formBlockMessage}
                    </p>
                  ) : null}
                  {formError ? (
                    <p className="epsf-list-page__error mt-2 mb-0">
                      {formError}
                    </p>
                  ) : null}
                </div>
                <div className="modal-footer py-2 epsf-return-modal__footer">
                  <span
                    className="epsf-return-modal__footer-note"
                    role="status"
                  >
                    Controlled destruction of IMP. Negative ledger movement.
                    Evidence optional (max {MAX_DESTROY_EVIDENCE} files).
                  </span>
                  <div className="epsf-return-modal__footer-actions">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={closeModal}
                      disabled={busy || requestBusy}
                    >
                      Cancel
                    </button>
                    {!destroyFromPending ? (
                      <button
                        type="button"
                        className={
                          needsRequest || formBlocked
                            ? "btn btn-primary"
                            : "btn btn-outline-primary"
                        }
                        onClick={onCreateRequest}
                        disabled={busy || requestBusy || formGateBusy}
                      >
                        {requestBusy ? "Creating..." : "Create request"}
                      </button>
                    ) : null}
                    <button
                      type="submit"
                      className={
                        !destroyFromPending && (needsRequest || formBlocked)
                          ? "btn btn-outline-primary"
                          : "btn btn-primary"
                      }
                      disabled={busy || requestBusy || formGateBusy || formBlocked}
                    >
                      {busy
                        ? "Saving..."
                        : formGateBusy
                          ? "Checking forms..."
                          : "Post destruction"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}
      <EpsfFormFillModal
        open={Boolean(formFillModal?.formInstanceId)}
        formInstanceId={formFillModal?.formInstanceId || ""}
        workItem={formFillModal?.workItem || null}
        mode={formFillModal?.mode || "fill"}
        onClose={() => setFormFillModal(null)}
        onSubmitted={async () => {
          setFormFillModal(null);
          refreshFormDues();
        }}
        onApproved={async () => {
          setFormFillModal(null);
          refreshFormDues();
        }}
      />
      <InfoAlert {...alertState} onClose={hideAlert} />
    </EpsfAccessGate>
  );
}
